import React, { useState } from 'react';

import { Form, Formik } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import PasswordFieldTransparent from '@/components/shared/form/PasswordFieldTransparent';
import PromptMessage from '@/components/UI/PromptMessage';
import { selectUserBtag } from '@/store/selectors/combined.selectors';
import { logException } from '@/utils/logger';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import { loginSuccess } from '@/store/actions/auth';
import { triggerNetreferSignup } from '@/shared/netrefer';
import { selectRegistrationOpenedFrom } from '@/store/ui/selectors';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import {
  HomeHeroAuthenticationStep,
  LoginFlowVariant,
  useHomeHeroAuthenticationStepActions,
} from '@/store/authentication/AuthenticationStore';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import { BffErrorCode, BffRegisterResponse } from '@lucky7ventures/bff-types';
import PhoneNumberField from '@/components/form/phone-number/PhoneNumberField';
import BffApiService from '@/shared/bffApiService';
import { BffApiError } from '@/models/bff-api-error';
import { MobileInputModel } from '@/models/mobile-input.model';
import TextInputFieldTransparent from '@/components/shared/form/TextInputFieldTransparent';
import { Button } from '@/components/buttons/Button';
import { useLogin } from '@/hooks/useLogin';
import StorageManager, { ReturningUserLoginMethod } from '@/shared/storageManager';
import { cdnImage } from '@/utils/image-utils';
import { sendEventToAppWrapper } from '@/utils/app-wrapper-utils';
import { useModal } from '@/features/modal/useModal';
import { MODALS } from '@/features/modal/types';

type FormValues = {
  fullName: string;
  mobile: MobileInputModel;
  password: string;
};

const RegistrationThailandForm = () => {
  const [error, setError] = useState<string | undefined>();
  const intl = useIntl();
  const dispatch = useDispatch();
  const btag = useSelector(selectUserBtag);
  const registrationOpenedFrom = useSelector(selectRegistrationOpenedFrom);
  const navigate = useAbsoluteNavigate();
  const { request, loading, success } = useBffApiRequest<BffRegisterResponse>();
  const { openLogin } = useLogin();
  const { setAuthenticationCredentials, setHomeHeroAuthenticationStep, setPostRegisterData } =
    useHomeHeroAuthenticationStepActions();
  const { openModal } = useModal();

  const handleLogin = () => {
    triggerGTag(GTagEvents.home_auth_login_click);
    openLogin({
      openedFrom: OpenedFrom.RegistrationThailand,
      variant: LoginFlowVariant.Mobile,
    });
  };

  const initialFormValues: FormValues = {
    fullName: '',
    mobile: {
      number: '',
      country: 'TH',
      prefix: '+66',
      wholeNumber: '',
    },
    password: '',
  };

  const formSchema = Yup.object().shape({
    fullName: Yup.string()
      .matches(
        /^[\u0E00-\u0E7Fa-zA-Z.\-']+( [\u0E00-\u0E7Fa-zA-Z.\-']+)+$/,
        intl.formatMessage({ id: 'fastReg.name.error.invalid' }),
      )
      .trim()
      .required(),
    mobile: Yup.object().shape({
      wholeNumber: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
      number: Yup.string().min(5, intl.formatMessage({ id: 'inputs.mobile.invalid' })),
    }),
    password: Yup.string()
      .test(
        'no-whitespace',
        intl.formatMessage({ id: 'fastReg.password.whitespaces.invalid' }),
        value => !(value && /\s/.test(value)),
      )
      .matches(
        /^(?!.*\s)(?=.*\d)(?=.*\p{L}).{8,25}$/u,
        intl.formatMessage({ id: 'fastReg.password.invalid' }),
      )
      .required(),
  });

  const handleSubmit = (values: FormValues) => {
    const handleError = (error: BffApiError, fullError: unknown): void => {
      const { code } = error;
      if (
        code === BffErrorCode.GIG_USERNAME_ALREADY_EXISTS ||
        code === BffErrorCode.GIG_MOBILE_AND_PREFIX_COMBINATION_ALREADY_REGISTERED
      ) {
        openModal(MODALS.BASIC, {
          title: intl.formatMessage({ id: 'fastReg.existingUser.title' }),
          body: intl.formatMessage({ id: 'fastReg.existingUser.body' }),
          children: (
            <img
              src={cdnImage('sitting-cat.png', {
                width: '200',
                height: '200',
                fit: 'scale-down',
              })}
              alt="Lucky Cat Sitting"
              className="mx-auto py-4"
            />
          ),
          btnText: intl.formatMessage({ id: 'login.login' }),
          onBtnClick: () => {
            triggerGTag(GTagEvents.fast_reg_existing_acc_login_click);
            handleLogin();
          },
          onClose: () => triggerGTag(GTagEvents.fast_reg_existing_acc_close),
        });
        triggerGTag(GTagEvents.fast_reg_existing_acc_show, {
          errorId: error.code,
        });
      } else if (code === BffErrorCode.GIG_ACCOUNT_BLOCKED) {
        openModal(MODALS.ACCOUNT_BLOCKED);
        triggerGTag(GTagEvents.thai_reg_account_blocked, { errorId: code });
      } else if (code === BffErrorCode.INVALID_PAYLOAD) {
        setError(
          intl.formatMessage({
            id: 'error.register.validation',
          }),
        );
        triggerGTag(GTagEvents.fast_reg_error_th, { errorId: code });
      } else {
        setError(
          `${intl.formatMessage({
            id: 'failedSignup.SomethingWentWrongMessage',
          })} (${code})`,
        );
        triggerGTag(GTagEvents.fast_reg_error_th, { errorId: code });
        logException(fullError || error);
      }
    };

    const handleSuccess = (username: string, password: string) => (data: BffRegisterResponse) => {
      StorageManager.setIsReturningUser(ReturningUserLoginMethod.Mobile);
      if (data.isTwoFactorAuthentication) {
        triggerGTag(GTagEvents.register_2fa);
        setAuthenticationCredentials(username, password);
        setPostRegisterData(data.userId, btag);
        setHomeHeroAuthenticationStep(HomeHeroAuthenticationStep.TwoFactorAuthentication);
      } else {
        dispatch(loginSuccess(data.token!, { isRegistration: true }));
        triggerGTag(GTagEvents.fast_reg_success, { userId: data.userId });
        sendEventToAppWrapper(GTagEvents.fast_reg_success, {
          userId: data.userId,
        });
        triggerNetreferSignup(btag, data.userId, 'th');
        // do specific redirects for some cases if registration was started on specific place
        if (registrationOpenedFrom === OpenedFrom.RewardsWheel) {
          navigate('/rewards-wheel');
        }
      }
    };

    const normalizedMobilePrefix = values.mobile.prefix.replace(/\D/g, '');
    const normalizedMobile = values.mobile.number.replace(/\D/g, '');
    const fullNormalizedMobile = normalizedMobilePrefix + normalizedMobile;

    request({
      apiMethod: BffApiService.registerThailand,
      payload: {
        fullName: values.fullName,
        mobile: values.mobile.number,
        mobilePrefix: values.mobile.prefix,
        password: values.password,
        btag,
      },
      successCallback: handleSuccess(fullNormalizedMobile, values.password),
      errorCallback: handleError,
    });
  };

  return (
    <>
      {error && (
        <div className="[&_p]:!text-base">
          <PromptMessage type="error" message={error} />
        </div>
      )}
      <Formik
        initialValues={initialFormValues}
        validationSchema={formSchema}
        validateOnMount
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ isValid }) => {
          return (
            <Form
              id="e2e_fastRegTh"
              autoComplete="off"
              className="flex flex-col gap-4 [&>button:last-of-type]:!mx-0 [&>button:last-of-type]:min-h-[50px]"
            >
              <TextInputFieldTransparent
                type="text"
                name="fullName"
                label={intl.formatMessage({ id: 'inputs.fullName' })}
                onFocus={() => triggerGTag(GTagEvents.fast_reg_th_name_click)}
                onValidationError={error =>
                  triggerGTag(GTagEvents.fast_reg_th_name_error, { error })
                }
                className="capitalize"
              />
              <PhoneNumberField
                label={intl.formatMessage({ id: 'inputs.mobile' })}
                country={'th'}
                name="mobile"
                onFocus={() => triggerGTag(GTagEvents.fast_reg_th_mobile_click)}
                onValidationError={error =>
                  triggerGTag(GTagEvents.fast_reg_th_mobile_error, { error })
                }
                className="border-[1px] border-white/30 text-white focus-within:border-blue-light
                [&_input]:placeholder:!text-white/60 mb-3"
              />
              <PasswordFieldTransparent
                label={intl.formatMessage({
                  id: 'inputs.password',
                })}
                name="password"
                onFocus={() => triggerGTag(GTagEvents.fast_reg_th_password_click)}
                onValidationError={error =>
                  triggerGTag(GTagEvents.fast_reg_th_password_error, { error })
                }
                autoComplete="new-password"
              />
              <Button
                text={
                  !loading && success
                    ? intl.formatMessage({ id: 'fastReg.success' })
                    : intl.formatMessage({ id: 'header.register' })
                }
                type="submit"
                btnType="primary"
                data-cy="register-th-submit-button"
                isLoading={loading}
                disabled={!isValid}
                onClickHandler={() => triggerGTag(GTagEvents.fast_reg_th_cta)}
              />
            </Form>
          );
        }}
      </Formik>
      <div className="text-base text-white mt-3">
        <FormattedMessage
          id="fastReg.login"
          values={{
            a: str => (
              <span
                data-cy="registration-form-switch-login-button"
                onClick={handleLogin}
                className="text-blue-lighter font-bold underline cursor-pointer"
              >
                {str}
              </span>
            ),
          }}
        />
      </div>
      <p className="mt-3 block mb-0 !text-[12px] italic text-white">
        <FormattedMessage
          id="fastReg.consent.text"
          values={{
            a: str => (
              <span
                onClick={() => navigate('/terms-and-conditions')}
                className="text-blue-lighter font-bold underline cursor-pointer"
              >
                {str}
              </span>
            ),
          }}
        />
      </p>
    </>
  );
};

export default RegistrationThailandForm;
